import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  setValidationErrors,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import { LoadingState } from '../../../../../../common/global/models';
import Button from '../../../../../../common/widgets/Button';
import Dialog from '../../../../../../common/widgets/Dialog';
import Input from '../../../../../../common/widgets/Input';
import { ErrorState } from '../../../../models/Errors';
import { resources } from '../../../../resources/resources';
import { customerService } from '../../../../services/customer';

interface EditDeliveryAddressDialogProps {
  closeDialog: (reloadData?: boolean) => void;
  deliveryAddressApartment?: string;
  deliveryAddressBloc?: string;
  deliveryAddressEntrance?: string;
  deliveryAddressFloor?: string;
  deliveryAddressNo?: string;
  deliveryAddressStreet?: string;
  city?: string;
  county?: string;
  hasPersonalAddress: boolean;
}

export const EditDeliveryAddressDialog = (
  props: EditDeliveryAddressDialogProps,
) => {
  const { closeDialog } = props;
  const { t: translate } = useTranslation();

  const [county, setCounty] = useState(props.county);
  const [city, setCity] = useState(props.city);
  const [deliveryAddressNo, setDeliveryAddressNo] = useState(
    props.deliveryAddressNo,
  );
  const [deliveryAddressStreet, setDeliveryAddressStreet] = useState(
    props.deliveryAddressStreet,
  );
  const [deliveryAddressBloc, setDeliveryAddressBloc] = useState(
    props.deliveryAddressBloc,
  );
  const [deliveryAddressEntrance, setDeliveryAddressEntrance] = useState(
    props.deliveryAddressEntrance,
  );
  const [deliveryAddressFloor, setDeliveryAddressFloor] = useState(
    props.deliveryAddressFloor,
  );

  const [deliveryAddressApartment, setDeliveryAddressApartment] = useState(
    props.deliveryAddressApartment,
  );

  const [saveButtonState, setSaveButtonState] = useState<LoadingState>(
    LoadingState.Ready,
  );
  const [errors, setErrors] = useState<ErrorState>({});

  const updateDeliveryAddress = async () => {
    setSaveButtonState(LoadingState.Requested);
    const response = await customerService.updateDeliveryAddress({
      county,
      city,
      deliveryAddressNo,
      deliveryAddressStreet,
      deliveryAddressApartment,
      deliveryAddressBloc,
      deliveryAddressEntrance,
      deliveryAddressFloor,
    });
    setSaveButtonState(LoadingState.Ready);

    if (!response.hasErrors) {
      closeDialog(true);
      showSuccessToast(translate(resources.saveConfirmation));
    } else {
      setValidationErrors(response, setErrors);
    }
  };

  const clearErrors = (name: string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[name] = [];
        }
      }),
    );
  };

  return (
    <Dialog closeDialog={closeDialog} showCloseIcon className="dialog-l">
      <div className="dialog-content">
        <div className="dialog-title">
          {props.hasPersonalAddress
            ? translate(resources.accountInfo.deliveryAddress.edit.title)
            : translate(resources.accountInfo.deliveryAddress.add.title)}
        </div>
        <div className="row">
          <div className="col col-6 col-sm-12">
            <Input
              label={translate(resources.accountInfo.deliveryAddress.county)}
              value={county}
              mandatory={true}
              onChange={e => setCounty(e.target.value?.toString())}
              errors={errors.county}
              clearErrors={() => clearErrors('county')}
            />
          </div>
          <div className="col col-6 col-sm-12">
            <Input
              label={translate(resources.accountInfo.deliveryAddress.city)}
              value={city}
              mandatory={true}
              onChange={e => setCity(e.target.value?.toString())}
              errors={errors.city}
              clearErrors={() => clearErrors('city')}
            />
          </div>
        </div>

        <div className="row">
          <div className="col col-6 col-sm-12">
            <Input
              label={translate(resources.accountInfo.deliveryAddress.street)}
              value={deliveryAddressStreet}
              mandatory={true}
              onChange={e =>
                setDeliveryAddressStreet(e.target.value?.toString())
              }
              errors={errors.deliveryAddressStreet}
              clearErrors={() => clearErrors('deliveryAddressStreet')}
            />
          </div>
          <div className="col col-6 col-sm-12">
            <Input
              label={translate(resources.accountInfo.deliveryAddress.number)}
              value={deliveryAddressNo}
              mandatory={true}
              onChange={e => setDeliveryAddressNo(e.target.value?.toString())}
              errors={errors.deliveryAddressNo}
              clearErrors={() => clearErrors('deliveryAddressNo')}
            />
          </div>
        </div>

        <div className="row">
          <div className="col col-6 col-sm-12">
            <Input
              label={translate(resources.accountInfo.deliveryAddress.bloc)}
              value={deliveryAddressBloc}
              onChange={e => setDeliveryAddressBloc(e.target.value?.toString())}
              errors={errors.deliveryAddressBloc}
              clearErrors={() => clearErrors('deliveryAddressBloc')}
            />
          </div>
          <div className="col col-6 col-sm-12">
            <Input
              label={translate(resources.accountInfo.deliveryAddress.entrance)}
              value={deliveryAddressEntrance}
              onChange={e =>
                setDeliveryAddressEntrance(e.target.value?.toString())
              }
              errors={errors.deliveryAddressEntrance}
              clearErrors={() => clearErrors('deliveryAddressEntrance')}
            />
          </div>
        </div>

        <div className="row">
          <div className="col col-6 col-sm-12">
            <Input
              label={translate(resources.accountInfo.deliveryAddress.floor)}
              value={deliveryAddressFloor}
              onChange={e =>
                setDeliveryAddressFloor(e.target.value?.toString())
              }
              errors={errors.deliveryAddressFloor}
              clearErrors={() => clearErrors('deliveryAddressFloor')}
            />
          </div>
          <div className="col col-6 col-sm-12">
            <Input
              label={translate(resources.accountInfo.deliveryAddress.apartment)}
              value={deliveryAddressApartment}
              onChange={e =>
                setDeliveryAddressApartment(e.target.value?.toString())
              }
              errors={errors.deliveryAddressApartment}
              clearErrors={() => clearErrors('deliveryAddressApartment')}
            />
          </div>
        </div>
      </div>

      <div className="dialog-actions horizontal">
        <Button
          onClick={updateDeliveryAddress}
          className="btn btn-solid"
          loading={saveButtonState !== LoadingState.Ready}
        >
          <span className="btn-text">{translate(resources.save)}</span>
        </Button>
        <Button onClick={() => closeDialog()} className="btn btn-outline">
          <span className="btn-text"> {translate(resources.close)}</span>
        </Button>
      </div>
    </Dialog>
  );
};
